import { FilterChain, useDynamicContext, useFunding, useSwitchWallet, useUserWallets, useWalletOptions } from "@dynamic-labs/sdk-react-core";
import { WalletIcon as DynamicWalletIcon } from '@dynamic-labs/wallet-book';
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { ThemedButton } from "../../../shared_components/ThemedButton";
import AnimatedLoader from "../../../shared_components/AnimatedLoader";
import { Box } from "../../../shared_components/UI/Box";
import { P, Span } from "../../../shared_components/Library/Typeography";
import { EthBalance, useEthBalance } from "../../../shared_components/Library/Buttons/WalletActiveButton";
import { useSelector } from "react-redux";
import { selectEthUsdPrice } from "../../../modules/web3/selectors";
import useWeb3 from "../../../hooks/useWeb3";
import { Divider } from ".";
import styled from "styled-components";
import { obfuscateEthWalletAddress } from "../../../shared_components/SocialConnectButtons/Twitter";
import Copy02 from "../../../shared_components/Icons/Copy02";
import { copyToClipboard } from "../Vault";
import ChevronDown from "../../../shared_components/Icons/ChevronDown";

const WalletWidget = () => {
  return (
    <WalletWidgetBox dc>
      <WalletListWidget />
      <BalanceWidget />
    </WalletWidgetBox>
  )
}

const WalletWidgetBox = styled(Box)`
background: #121212;
border-radius: var(--units-12, 12px);
border: 1px solid var(--stroke-tertiary, #252525);
`

function WalletListWidget() {
  const { primaryWallet } = useDynamicContext()
  const userWallets = useUserWallets()
  const { getFilteredWalletOptions } = useWalletOptions()
  const switchWallet = useSwitchWallet();

  const onWalletClick = useCallback((walletId) => () => {
    switchWallet(walletId)
  }, [switchWallet])

  const _walletOptions = getFilteredWalletOptions(FilterChain('EVM')).reduce((acc, walletOption) => {
    const walletMatch = userWallets.find(w => w.key === walletOption.key)
    if (walletMatch) {
      return [...acc, { ...walletOption, ...walletMatch }]
    }
    return [...acc]
  }, [])
  const _otherWallets = userWallets.filter(w => !_walletOptions.find(o => o.id === w.id))
  const wallets = [..._walletOptions, ..._otherWallets]
  const listWallets = wallets.filter(w => w.id !== primaryWallet?.id).filter(w => w.key !== 'turnkeyhd')
  const currentWallet = wallets.find(w => w.id === primaryWallet?.id)

  const [showList, setShowList] = useState(false)
  const toggleList = useCallback(() => {
    setShowList(!showList)
  }, [showList])

  return (
    <Box dc>
      <WalletBox
        key={currentWallet?.id}
        wallet={currentWallet}
        onClick={toggleList}
        isSelected
        isExpanded={showList}
        isExpandable={listWallets.length >= 1}
      />
      <ExpandableBox isExpanded={showList} dc>
        {listWallets.map((wallet, index) => (
          <>
            <WalletBox
              key={wallet.id}
              wallet={wallet}
              onClick={onWalletClick(wallet.id)}
              isSelected={false}
              isExpanded={false}
              isExpandable={false}
            />
            {index !== listWallets.length - 1 && <Divider w="100%" h="1px" padH="l" c="secondary" />}
          </>
        ))}
      </ExpandableBox>
    </Box>
  )
}

const ExpandableBox = styled(Box) <{ isExpanded: boolean }>`
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  flex: unset;

  max-height: ${({ isExpanded }) => (isExpanded ? '2000px' : '0')}; /* Use a value larger than the max expanded height */
`

function WalletBox({ wallet, onClick, isSelected, isExpanded, isExpandable }) {
  const walletName = wallet.name ?? wallet.connector?.name

  const onCopy = () => {
    copyToClipboard(wallet.address)
    toast.success('Copied to clipboard')
  }

  return (
    <StyledWalletBox key={wallet.id} padH="l" padT={20} padB={isSelected ? 20 : 16} onClick={onClick} isSelected={isSelected}>
      <Box g={8} ac>
        <WalletIcon walletKey={wallet.key} />
        <Box dc g={2}>
          {isSelected ? (
            <Box g={4} ac>
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCopy()
                }}
                style={{ cursor: 'pointer' }}
              >
                <Copy02 color="var(--text-primary, #FFF)" size={18} />
              </div>
              <P s="L" c="text-primary" fw="semibold">{obfuscateEthWalletAddress(wallet.address)}</P>
            </Box>
          ) : (
            <P s="L" c="text-primary" fw="semibold">{obfuscateEthWalletAddress(wallet.address)}</P>
          )}
          <Span s="S" c="text-secondary">
            {walletName === 'ZeroDev' ? 'Smart Wallet (ZeroDev)' : walletName}
          </Span>
        </Box>
        {isSelected && isExpandable && (
          <Box ac fu aw padV={12} padH={12}>
            <RotateIcon isExpanded={isExpanded}>
              <ChevronDown $color="var(--text-primary, #FFF)" size={18} />
            </RotateIcon>
          </Box>
        )}
      </Box>
    </StyledWalletBox>
  )
}

const RotateIcon = styled.div<{ isExpanded: boolean }> `
  transition: transform 0.3s ease-in-out;
  transform: ${({ isExpanded }) => (isExpanded ? "rotate(0deg)" : "rotate(180deg)")};
`;

const StyledWalletBox = styled(Box) <{ isSelected?: boolean }>`
  background: #2b2b2b;

  ${({ isSelected }) =>
    isSelected &&
    `
      background: var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04));
    `}

  :hover {
    cursor: pointer;
    background: var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.06));
  }
`

function WalletIcon({ walletKey }) {
  if (['zerodev', 'turnkeyhd'].includes(walletKey)) {
    return (
      <img
        src="/img/icons/chains/white/ethereum.svg"
        alt="ethereum"
        style={{ height: '40px', width: '40px' }}
      />
    )
  }

  return (
    <DynamicWalletIcon
      walletKey={walletKey}
      style={{
        height: '40px',
        width: '40px',
      }}
    />
  )
}

function BalanceWidget() {
  return (
    <Box dc g={16} pad="l">
      <Balance />
      <FundWalletButton />
    </Box>
  )
}

function Balance() {
  const ethPriceInUsd = useSelector(selectEthUsdPrice)
  const ethBalance = useEthBalance()

  return (
    <Box dc c g={4}>
      <Span s="S" fw="regular" c="text-secondary">Balance</Span>
      <Box c aw>
        <EthBalance iconSize={29} s="2XL" />
      </Box>
      <Span s="S" fw="regular" c="text-secondary">${ethBalance > 0 ? (ethBalance * ethPriceInUsd).toFixed(2) : "0.00"}</Span>
    </Box>
  )
}

function FundWalletButton() {
  const { enabled } = useFunding();
  const { openOnramp } = useWeb3()
  const [loading, setLoading] = useState(false)

  const onClick = useCallback(async () => {
    if (!openOnramp) {
      console.warn('No onramp function')
      return
    }

    try {
      setLoading(true)
      openOnramp()
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [openOnramp]);

  return (
    <ThemedButton w="100%" $styleType="primary" disabled={!enabled || loading} onClick={onClick}>
      {loading ? <AnimatedLoader /> : "Deposit"}
    </ThemedButton>
  )
}

export default WalletWidget
